import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGetVaHospitalOrderVolumeQuery } from "../../app/vaApi/vaApi";
import { extractHospitalOrderVolumeFromResult } from "../../helpers/vaMetricsUtils";
import {
  DISTRIBUTOR_METRICS_DEFAULT_FILTER,
  DISTRIBUTOR_METRICS_EMPTY_LABEL,
} from "../../helpers/metricsPeriod";
import MetricsPeriodFilter from "./MetricsPeriodFilter";
import Button from "../shared/buttons/button";
import Pagination from "../shared/pagination/Pagination";
import { shouldSkipAuthedQuery } from "../../helpers/authSession";

const PAGE_SIZE = 10;

const VOLUME_COLUMNS = [
  { key: "hospitalName", label: "Hospital", sortable: true },
  { key: "orderCount", label: "Orders", sortable: true },
  { key: "revenue", label: "Revenue", sortable: true },
  { key: "status", label: "Status", sortable: false },
];

function formatCurrency(value) {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatUnits(value) {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function SortIcon({ active, direction }) {
  if (!active) {
    return (
      <span className="hospital-volume-panel__sort-icon" aria-hidden="true">
        ↕
      </span>
    );
  }
  return (
    <span
      className="hospital-volume-panel__sort-icon is-active"
      aria-hidden="true"
    >
      {direction === "asc" ? "↑" : "↓"}
    </span>
  );
}

export default function HospitalOrderVolumePanel({
  filter = DISTRIBUTOR_METRICS_DEFAULT_FILTER,
  onFilterChange,
}) {
  const [sortKey, setSortKey] = useState("orderCount");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const resizingRef = useRef(null);

  const skip = shouldSkipAuthedQuery();
  const { data, isLoading, isError, refetch, isFetching } =
    useGetVaHospitalOrderVolumeQuery(filter, { skip });

  const payload = extractHospitalOrderVolumeFromResult(data) || {
    hospitals: [],
    totalHospitals: 0,
    totalOrders: 0,
  };

  const followUpCount = useMemo(
    () =>
      (payload.hospitals || []).filter((row) => Number(row.orderCount) === 0)
        .length,
    [payload.hospitals]
  );

  const sortedHospitals = useMemo(() => {
    const rows = [...(payload.hospitals || [])];
    const direction = sortDir === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      if (sortKey === "hospitalName") {
        return (
          String(a.hospitalName).localeCompare(String(b.hospitalName)) *
          direction
        );
      }
      if (sortKey === "revenue") {
        return (Number(a.revenue) - Number(b.revenue)) * direction;
      }
      return (Number(a.orderCount) - Number(b.orderCount)) * direction;
    });

    return rows;
  }, [payload.hospitals, sortKey, sortDir]);

  const totalItems = sortedHospitals.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE) || 1);

  useEffect(() => {
    setPage(1);
  }, [filter, sortKey, sortDir]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedHospitals = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedHospitals.slice(start, start + PAGE_SIZE);
  }, [sortedHospitals, page]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const stopResize = useCallback(() => {
    if (!resizingRef.current) return;
    resizingRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    const onMove = (event) => {
      const active = resizingRef.current;
      if (!active) return;
      const nextWidth = Math.max(
        48,
        active.startWidth + (event.clientX - active.startX)
      );
      setColumnWidths((prev) => ({ ...prev, [active.key]: nextWidth }));
    };

    const onUp = () => stopResize();

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [stopResize]);

  const handleResizeStart = (event, column, index) => {
    event.preventDefault();
    event.stopPropagation();

    const table = tableRef.current;
    if (!table) return;

    const headers = Array.from(table.querySelectorAll("thead th"));
    const measured = {};
    headers.forEach((th, i) => {
      const col = VOLUME_COLUMNS[i];
      if (col) measured[col.key] = th.getBoundingClientRect().width;
    });

    const key = column.key;
    setColumnWidths(measured);
    resizingRef.current = {
      key,
      startX: event.clientX,
      startWidth:
        measured[key] ||
        headers[index]?.getBoundingClientRect().width ||
        80,
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const tablePixelWidth = useMemo(() => {
    const widths = VOLUME_COLUMNS.map((col) => columnWidths[col.key]).filter(
      Boolean
    );
    if (widths.length !== VOLUME_COLUMNS.length) return undefined;
    return widths.reduce((sum, width) => sum + width, 0);
  }, [columnWidths]);

  const columnStyle = (key) => {
    const width = columnWidths[key];
    if (!width) return undefined;
    return { width: `${width}px`, minWidth: `${width}px` };
  };

  const showStats =
    !isError && !isLoading && !isFetching && sortedHospitals.length > 0;

  return (
    <section className="hospital-volume-panel">
      <div className="hospital-volume-panel__stats">
        {onFilterChange ? (
          <div className="hospital-volume-stat hospital-volume-stat--period distributor-metrics-card">
            <MetricsPeriodFilter
              value={filter}
              onChange={onFilterChange}
              emptyLabel={DISTRIBUTOR_METRICS_EMPTY_LABEL}
            />
          </div>
        ) : null}

        {showStats ? (
          <>
            <div className="hospital-volume-stat">
              <span className="hospital-volume-stat__label">Hospitals</span>
              <span className="hospital-volume-stat__value">
                {formatUnits(payload.totalHospitals)}
              </span>
            </div>
            <div className="hospital-volume-stat">
              <span className="hospital-volume-stat__label">Orders</span>
              <span className="hospital-volume-stat__value">
                {formatUnits(payload.totalOrders)}
              </span>
            </div>
            <div className="hospital-volume-stat hospital-volume-stat--alert">
              <span className="hospital-volume-stat__label">Need follow-up</span>
              <span className="hospital-volume-stat__value">
                {formatUnits(followUpCount)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {isError && !skip ? (
        <div className="hospital-volume-panel__state hospital-volume-panel__state--error">
          <span>Couldn’t load hospital volume.</span>
          <Button label="Retry" className="btn-grey" onClick={refetch} />
        </div>
      ) : isLoading || isFetching ? (
        <div className="hospital-volume-panel__state">Loading hospitals…</div>
      ) : sortedHospitals.length === 0 ? (
        <div className="hospital-volume-panel__state">
          No hospitals found for this period.
        </div>
      ) : (
        <div className="hospital-volume-panel__table-card">
          <div className="hospital-volume-panel__table-wrap">
            <table
              ref={tableRef}
              className={`hospital-volume-panel__table${
                tablePixelWidth ? " hospital-volume-panel__table--resized" : ""
              }`}
              style={
                tablePixelWidth
                  ? {
                      width: `${tablePixelWidth}px`,
                      minWidth: "100%",
                      tableLayout: "fixed",
                    }
                  : undefined
              }
            >
              <thead>
                <tr>
                  {VOLUME_COLUMNS.map((column, index) => {
                    const canSort = column.sortable !== false;
                    const isActive = sortKey === column.key;
                    return (
                      <th
                        key={column.key}
                        className="hospital-volume-panel__th"
                        style={columnStyle(column.key)}
                      >
                        {canSort ? (
                          <button
                            type="button"
                            className="hospital-volume-panel__sort"
                            onClick={() => toggleSort(column.key)}
                          >
                            {column.label}
                            <SortIcon
                              active={isActive}
                              direction={sortDir}
                            />
                          </button>
                        ) : (
                          column.label
                        )}
                        <span
                          className="datatable-col-resizer"
                          onMouseDown={(event) =>
                            handleResizeStart(event, column, index)
                          }
                          onClick={(event) => event.stopPropagation()}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label={`Resize ${column.label}`}
                        />
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pagedHospitals.map((row) => {
                  const isLow = Number(row.orderCount) === 0;
                  return (
                    <tr
                      key={`${row.hospitalName}-${row.clinicName || ""}`}
                      className={
                        isLow ? "hospital-volume-panel__row--low" : undefined
                      }
                    >
                      <td style={columnStyle("hospitalName")}>
                        <div className="hospital-volume-panel__name">
                          {row.hospitalName}
                        </div>
                        {row.clinicName &&
                          row.clinicName !== row.hospitalName && (
                            <div className="hospital-volume-panel__clinic">
                              {row.clinicName}
                            </div>
                          )}
                      </td>
                      <td style={columnStyle("orderCount")}>
                        <span
                          className={`hospital-volume-panel__metric${
                            isLow
                              ? " hospital-volume-panel__metric--zero"
                              : ""
                          }`}
                        >
                          {formatUnits(row.orderCount)}
                        </span>
                      </td>
                      <td style={columnStyle("revenue")}>
                        <span className="hospital-volume-panel__metric">
                          {formatCurrency(row.revenue)}
                        </span>
                      </td>
                      <td style={columnStyle("status")}>
                        {isLow ? (
                          <span className="hospital-volume-panel__badge hospital-volume-panel__badge--danger">
                            Follow up
                          </span>
                        ) : (
                          <span className="hospital-volume-panel__badge hospital-volume-panel__badge--ok">
                            Active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(totalItems > 0 || totalPages > 1) && (
            <div className="hospital-volume-panel__pagination">
              <div>
                {totalItems > 0 && (
                  <span>
                    Showing{" "}
                    <strong>
                      {(page - 1) * PAGE_SIZE + 1} –{" "}
                      {Math.min(page * PAGE_SIZE, totalItems)}
                    </strong>{" "}
                    of <strong>{totalItems}</strong>
                  </span>
                )}
              </div>
              {totalPages > 1 && (
                <Pagination
                  totalPages={totalPages}
                  currentPage={page}
                  onPageChange={setPage}
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
